import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
} from '~/shadcn/ui/dialog'
import { TwoFaVerifyForm } from '~/@mid_2fa/twofa-verify-form/TwoFaVerifyForm'
import { PhoneNumberForm } from '~/@mid_ui/phone-number-form/PhoneNumberForm'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { useChangePhoneNumber } from './useChangePhoneNumber'
import { useTwoFaCodeExpirationCountdown } from '~/@mid_2fa/twofa/useTwoFaCodeExpirationCountdown'
import { Text } from '~/@mid_ui/typography/Text'
import { Spinner } from '~/shadcn/ui/spinner'
import { Button } from '~/shadcn/ui/button/button'
import { CodeExpirationCountdown } from '~/@mid_2fa/twofa/CodeExpirationCountdown'

type Props = {
  onPhoneNumberChanged: () => void
  disabled?: boolean
}

export function PhoneNumberChangeDialog({
  onPhoneNumberChanged,
  disabled,
}: Props) {
  const {
    step,
    isDialogOpen,
    handleOpenDialog,
    handleCloseDialog,
    verifiedPhoneNumber,
    changePhoneNumber: changePhoneNumberMutation,
    isRequestingChangePhoneNumber,
    changePhoneNumberError,
    changePhoneNumberCodeExpiresAt,

    changePhoneNumberVerify: changePhoneNumberVerifyMutation,
    isRequestingChangePhoneNumberVerify,
    changePhoneNumberVerifyError,
  } = useChangePhoneNumber(onPhoneNumberChanged)

  const { countdown, hasCodeExpired } = useTwoFaCodeExpirationCountdown(
    changePhoneNumberCodeExpiresAt
  )

  return (
    <>
      <Button variant="outline" onClick={handleOpenDialog} disabled={disabled}>
        Zmeniť telefónne číslo
      </Button>
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="px-5 md:px-12 py-12">
          <div className="flex flex-col items-center gap-2 text-center">
            <HeadingTwo className="text-2xl md:text-2xl text-center mb-2">
              Zmeniť telefónne číslo
            </HeadingTwo>
          </div>
          {step === 'credentials' && (
            <>
              <DialogDescription title="Zadajte nové telefónne číslo" />
              <PhoneNumberForm
                handleSubmitForm={changePhoneNumberMutation}
                isRequesting={isRequestingChangePhoneNumber}
                error={changePhoneNumberError}
                FooterComponent={(props) => (
                  <DialogFooter
                    className="flex flex-row justify-center sm:justify-center lg:flex-row mt-4 gap-4 w-full"
                    {...props}
                  />
                )}
              />
            </>
          )}

          {step === 'verify' && (
            <>
              {hasCodeExpired && (
                <div className="flex flex-col items-center gap-4 text-center">
                  <Text>SMS kód vypršal.</Text>
                  <Button
                    variant="outline"
                    onClick={() =>
                      verifiedPhoneNumber &&
                      changePhoneNumberMutation({ number: verifiedPhoneNumber })
                    }>
                    Znova odoslať SMS kód{` `}
                    {isRequestingChangePhoneNumber && (
                      <Spinner className="size-4" />
                    )}
                  </Button>
                </div>
              )}

              {!hasCodeExpired && (
                <>
                  <DialogDescription title="Zadajte SMS kód ktorý ste dostali na Vaše telefónne číslo. Kód si môžete dať znovu odoslať. Platnosť kódu je 3 minúty." />
                  <TwoFaVerifyForm
                    formContent={
                      <div className="flex flex-col justify-start items-start gap-2">
                        <p className="text-md text-center w-full">
                          Overte svoj účet zadaním SMS kódu,
                          <br />
                          ktorý vám bol zaslaný na číslo {verifiedPhoneNumber}.
                          <br />
                          <CodeExpirationCountdown countdown={countdown} />
                        </p>
                      </div>
                    }
                    fieldLabel="Potrvrdzovací SMS kód"
                    FooterComponent={(props) => (
                      <div
                        className="flex flex-col items-center justify-center mt-8 gap-2 w-full"
                        {...props}
                      />
                    )}
                    submitButtonLabel="Potvrdiť SMS kód"
                    handleSubmitForm={changePhoneNumberVerifyMutation}
                    isRequesting={isRequestingChangePhoneNumberVerify}
                    error={changePhoneNumberVerifyError}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
